import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    BulkSelectEmployeeDialogComponent,
    ConfirmationDialogComponent,
    DeprecatedConfirmationDialogComponent,
    DeprecatedModalFormComponent,
} from '@app/components';
import { FeatureFlag } from '@app/enums';
import { Status } from '@app/modules/performance/enums/status.enum';
import { ApiValidationService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { ShowView } from '@common/views';
import { Employee } from '@models/employee/employee.model';
import { ReviewResponse } from '@models/performance/review-response.model';
import { Review } from '@models/performance/review.model';
import { ReviewDisplayComponent } from '@performance/components/review-display/review-display.component';
import { ReviewResultsComponent } from '@performance/components/review-results/review-results.component';
import { PerformanceResolver } from '@performance/performance.resolver';
import { ReviewService } from '@performance/services/review.service';

@Component({
    templateUrl: './review-show.template.html',
    styleUrls: ['./review-show.style.scss'],
})
export class ReviewShowView extends ShowView implements OnInit {
    @ViewChild('reviewDisplayComponent') reviewDisplayComponent: ReviewDisplayComponent;
    @ViewChild('reviewResultsComponent', { static: true }) reviewResultsComponent: ReviewResultsComponent;
    @ViewChild('confirmDelete', { static: true }) confirmDelete: DeprecatedConfirmationDialogComponent;
    @ViewChild('appEmployeeSelection') appEmployeeSelection: BulkSelectEmployeeDialogComponent;
    @ViewChild('reviewDetailsModal') reviewDetailsModal: DeprecatedModalFormComponent;
    @ViewChild('confirmAccessChange') confirmAccessChange: DeprecatedConfirmationDialogComponent;
    @ViewChild('confirmForceComplete', { static: true }) confirmForceComplete!: ConfirmationDialogComponent;

    isLoading = true;
    Status = Status;

    editReview: string[] = Review.permission.edit;
    review: Review = this.route.snapshot.data.models.get('review');
    canEditThisReview = this.reviewService.userCanEditReview(this.review);
    settings: any;
    canViewResults = false;
    canCompleteReview = false;
    hasPerformanceReviewImprovementsFeatureFlag = false;
    responseAccessMessage = '';
    showReviewNoteSection = false;
    canViewOwnResponses = false;
    loggedInEmployee: Employee = null;
    canExportResults = false;

    constructor(
        private route: ActivatedRoute,
        private location: Location,
        private router: Router,
        private notify: NotifyService,
        private resolver: PerformanceResolver,
        public auth: AuthService,
        private reviewService: ReviewService,
        private validation: ApiValidationService,
        private featureService: FeatureService
    ) {
        super();

        if (!this.review.userResponse) {
            this.review.userResponse = new ReviewResponse();
        }
    }

    async ngOnInit(): Promise<void> {
        this.hasPerformanceReviewImprovementsFeatureFlag = await this.featureService.has(
            FeatureFlag.performanceReviewImprovements
        );

        this.loggedInEmployee = this.auth.employee;

        this.setViewPermissions();

        this.setCompletePermissions();

        this.showReviewNoteSection = this.hasPerformanceReviewImprovementsFeatureFlag && this.review.note !== undefined;

        this.canExportResults = this.hasPerformanceReviewImprovementsFeatureFlag && this.getExportPermission();

        this.isLoading = false;
    }

    reResolve(): void {
        this.isLoading = true;
        this.resolver.resolve(this.route.snapshot, this.router.routerState.snapshot).then((models) => {
            this.review = <Review>models.get('review');
            if (!this.review.userResponse) {
                this.review.userResponse = new ReviewResponse();
            }

            this.setViewPermissions();
            this.setCompletePermissions();

            this.isLoading = false;
        });
    }

    onEdit(): void {
        this.reviewDetailsModal.show();
    }

    onSaveAction(): void {
        if (
            this.review.isFieldClean('viewResultsBeforeCompletion') &&
            this.review.isFieldClean('participantsCanViewResults') &&
            this.review.isFieldClean('subjectCanViewResults')
        ) {
            this.onSave();
            return;
        }

        /**
         * When changing access permission, ensure admin confirms change.
         */
        const message = this.review.getResponseAccessMessage(true);
        this.confirmAccessChange.description =
            'Changing access permissions will effect existing and new review responses. ' + message;
        this.confirmAccessChange.show().then((confirmDelete) => {
            if (confirmDelete) {
                this.onSave();
            }
        });
    }

    onSave(): void {
        // load within modal so we can show errors without flash
        this.reviewDetailsModal.isLoading = true;
        this.review
            .save()
            .then(() => {
                this.reResolve();
                this.reviewDetailsModal.hide();
                this.reviewDetailsModal.isLoading = false;
                this.notify.success('performance.show.reviewSaved');
            })
            .catch((err) => {
                try {
                    this.validation.errors(this.reviewDetailsModal.form.form, err);
                } catch {
                    this.notify.error(err.message || 'performance.show.saveOrUpdateError');
                }
                this.reviewDetailsModal.isLoading = false;
            });
    }

    onCancel(): void {
        this.review.revert();
    }

    onDelete(): void {
        this.confirmDelete.show().then((confirmDelete) => {
            if (confirmDelete) {
                this.isLoading = true;
                this.review
                    .delete()
                    .then(() => {
                        this.notify.success('performance.show.reviewDeleted');
                        this.location.back();
                    })
                    .catch((err) => {
                        this.notify.error(err);
                        this.isLoading = false;
                    });
            }
        });
    }

    /**
     * Update which employees are assigned to this review
     */
    onAssign(): void {
        this.appEmployeeSelection.show<Employee[]>([...this.review.participants]).then((employees) => {
            if (employees) {
                this.review.sync('participants', employees);
                this.onSave();
            }
        });
    }

    setViewPermissions(): void {
        this.hasPerformanceReviewImprovementsFeatureFlag
            ? this.setResponseAccessStatusV2()
            : this.setResponseAccessStatus();

        this.canViewOwnResponses =
            this.hasPerformanceReviewImprovementsFeatureFlag &&
            !this.canViewResults &&
            this.review.userIsParticipant &&
            this.review.userAssignment?.response?.submittedAt;
    }

    setCompletePermissions(): void {
        this.canCompleteReview =
            !this.review.isComplete &&
            this.review.userIsParticipant &&
            !this.review.userAssignment?.response?.submittedAt;
    }

    setResponseAccessStatus(): void {
        this.responseAccessMessage = this.review.getResponseAccessMessage();

        /**
         * Current user is the admin
         */
        if (this.auth.isAdmin()) {
            this.canViewResults = true;
            return;
        }

        /**
         * Current user is subject, base view on flag only
         */
        if (this.review.employeeIsSubject(this.auth.employee)) {
            this.canViewResults = this.review.subjectCanViewResults;
            return;
        }

        /**
         * Is user a participant, and flag is enabled.
         * Should not include subject, order with above is important (that behaviour defined by own flag)
         */
        if (this.review.participantsCanViewResults && this.review.employeeIsParticipant(this.auth.user)) {
            this.canViewResults = true;
            return;
        }

        // Current user can access the review subject (e.g is subjects manager)
        if (this.auth.canAccessEmployee(this.review.subject)) {
            this.canViewResults = true;
            return;
        }

        this.canViewResults = false;
    }

    setResponseAccessStatusV2(): void {
        // By default, admins can always see review responses no matter if the review is completed.
        if (this.auth.isAdmin()) {
            this.canViewResults = true;
            return;
        }

        // Direct Managers: Can view review responses once the review has been completed by all participants.
        if (
            this.review.isComplete &&
            !this.review.viewResultsBeforeCompletion &&
            this.auth.canAccessEmployee(this.review.subject)
        ) {
            this.canViewResults = true;
            return;
        }

        // Direct Managers: Can view submitted review responses, even when reviews are in progress.
        if (this.review.viewResultsBeforeCompletion && this.auth.canAccessEmployee(this.review.subject)) {
            this.canViewResults = true;
            return;
        }

        // Make results visible to reviewees themselves.
        if (this.review.employeeIsSubject(this.auth.employee)) {
            this.canViewResults = this.review.isComplete && this.review.subjectCanViewResults;
            return;
        }

        // Make results visible to other review participants.
        if (this.review.employeeIsParticipant(this.auth.employee)) {
            this.canViewResults = this.review.isComplete && this.review.participantsCanViewResults;
            return;
        }
    }

    canViewScore(): boolean {
        if (this.auth.isAdmin() || this.auth.user.is(this.review.creator)) {
            return true;
        }

        if (this.auth.user.is(this.review.subject)) {
            return this.settings.show_scores;
        }

        return this.auth.canAccessEmployee(this.review.subject);
    }

    remind(): void {
        this.isLoading = true;
        this.review
            .remind()
            .then(() => {
                this.notify.success('performance.show.remindersSent');
                this.isLoading = false;
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err);
            });
    }

    async forceComplete(): Promise<void> {
        const response = await this.confirmForceComplete.open();
        if (!response) {
            return;
        }

        this.isLoading = true;
        this.review
            .force()
            .then(() => {
                this.reResolve();
            })
            .catch((err) => {
                this.notify.error(err);
            })
            .finally(() => {
                this.isLoading = false;
            });
    }

    launch(): void {
        this.reviewDisplayComponent.launch();
    }

    results(onlyForEmployee: Employee = null): void {
        this.reviewResultsComponent.launch(onlyForEmployee);
    }

    getReviewEditLink(): string[] {
        return ['/performance', 'reviews', this.review.id.toString(), 'edit'];
    }

    onResponseSave(response: ReviewResponse): void {
        // stop the issue where submitted reviews are autosaved
        if (response.autoSaved && response.submittedAt) {
            return;
        }

        if (!response.autoSaved) {
            this.isLoading = true;
        }

        response
            .param('review', this.review.id)
            .save()
            .then((r) => {
                this.review.userResponse.id = r.id;
                if (!response.autoSaved) {
                    this.notify.success('performance.show.reviewUpdated');
                    this.reResolve();
                    return;
                }
                this.isLoading = false;
            })
            .catch((err) => {
                this.isLoading = false;
                if (this.reviewDisplayComponent.isActive) {
                    this.reviewDisplayComponent.exit(false);
                }
                this.notify.error(err);
            });
    }

    private getExportPermission(): boolean {
        if (this.auth.isAdmin()) {
            return true;
        }

        if (this.review.subject.reportsTo === this.auth.employee.id) {
            return !!(this.review.completedAt || this.review.viewResultsBeforeCompletion);
        }

        return this.review.completedAt && this.review.creator.id === this.auth.employee.id;
    }
}
